import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import Footer from './Footer';
import useCredits from '../hooks/useCredits';
import { db, createCheckoutSession, listCreditPacks } from '../firebase';
import { withRetry, toUserMessage } from '../utils/retry';
import { trackEvent } from '../analytics';
import { formatCredits, topUpLabel } from '../pricing/topups';
import './PurchasePage.css';

function PurchasePage({ user, signIn }) {
  const { credits, loading: creditsLoading } = useCredits(user);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState(null);
  const [packsReloadKey, setPacksReloadKey] = useState(0);

  const retryLoadPacks = useCallback(() => {
    setPacksReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPacksLoading(true);
    setPacksError(null);
    withRetry(() => listCreditPacks())
      .then((result) => {
        if (!cancelled) {
          setPacks(result.data?.packs || []);
          setPacksLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPacksError(toUserMessage(err, 'Failed to load credit top-ups.'));
          setPacksLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [packsReloadKey]);

  useEffect(() => {
    if (!user) { setTransactions([]); setTxnLoading(false); return; }
    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(txnRef, orderBy('created_at', 'desc'), limit(5));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTransactions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setTxnLoading(false);
    }, (err) => {
      console.error("Error fetching transactions:", err);
      setTxnLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  const handlePurchase = async (packId) => {
    setError(null);
    setPurchasing(packId);
    try {
      const successUrl = `${window.location.origin}/purchase/success`;
      const cancelUrl = `${window.location.origin}/credits`;
      const result = await withRetry(() =>
        createCheckoutSession({ pack_id: packId, success_url: successUrl, cancel_url: cancelUrl })
      );
      trackEvent('begin_checkout', { pack_id: packId });
      window.location.href = result.data.url;
    } catch (err) {
      setError(toUserMessage(err, 'We couldn\'t start checkout. Please try again.'));
      setPurchasing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="purchase-page">
      <Header user={user} signIn={signIn} />
      <div className="purchase-content">
        <h1>Credit top-ups</h1>
        <p className="purchase-subtitle">
          Add credits on top of your plan’s monthly allowance — they never expire.
        </p>

        {!user ? (
          <div className="purchase-signin-prompt">
            <p>Sign in to buy a top-up and start creating.</p>
            <button className="btn-teal" onClick={signIn}>Sign In</button>
          </div>
        ) : (
          <>
            {!creditsLoading && credits && (
              <div className="current-balance">
                <span>Current Balance: </span>
                <strong>{credits.balance} credits</strong>
              </div>
            )}

            {error && <div className="purchase-error" role="alert">{error}</div>}

            {packsLoading ? (
              <div className="credit-packs-loading">
                <LoadingSpinner size="large" label="Loading top-ups..." block />
              </div>
            ) : packsError ? (
              <div className="credit-packs-error" role="alert">
                <p>{packsError}</p>
                <button type="button" className="btn-teal" onClick={retryLoadPacks}>
                  Try again
                </button>
              </div>
            ) : (
              <div className="credit-packs">
                {packs.map((pack) => (
                  <div
                    key={pack.id}
                    className={`credit-pack${pack.featured ? ' credit-pack-featured' : ''}`}
                    data-testid={`credit-pack-${pack.id}`}
                  >
                    {pack.featured && <div className="credit-pack-badge">Best Value</div>}
                    {/* Labelled by amount, derived from `credits` — never from
                        the server's `name`, so a stale Stripe/JSON name can't
                        reintroduce the pack-vs-tier "Pro" collision here. The
                        amount is the card's headline, so the old separate
                        big-number row would just repeat it. */}
                    <div className="credit-pack-name">
                      <span data-testid={`credit-pack-credits-${pack.id}`}>
                        {topUpLabel(pack.credits)}
                      </span>
                    </div>
                    <div className="credit-pack-price">${pack.price_usd}</div>
                    <div className="credit-pack-per-credit">
                      ${(pack.price_usd / pack.credits).toFixed(2)}/credit
                    </div>
                    <button
                      className={`credit-pack-cta${pack.featured ? ' credit-pack-cta-featured' : ''}`}
                      onClick={() => handlePurchase(pack.id)}
                      disabled={purchasing !== null}
                    >
                      {purchasing === pack.id
                        ? 'Redirecting...'
                        : `Buy ${formatCredits(pack.credits)} credits`}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="transaction-history">
              <h2>Recent Transactions</h2>
              {txnLoading ? (
                <div className="no-transactions">
                  <LoadingSpinner label="Loading transactions..." />
                </div>
              ) : transactions.length === 0 ? (
                <div className="no-transactions">No transactions yet</div>
              ) : (
                <>
                  <table className="transaction-table transaction-desktop">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td>{formatDate(txn.created_at)}</td>
                          <td>{txn.type}</td>
                          <td>{txn.description}</td>
                          <td className={(txn.amount || 0) >= 0 ? 'txn-positive' : 'txn-negative'}>
                            {(txn.amount || 0) >= 0 ? '+' : ''}{txn.amount || 0}
                          </td>
                          <td>{txn.balance_after || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="transaction-cards-preview">
                    {transactions.map((txn) => (
                      <div key={txn.id} className="transaction-card">
                        <div className="txn-card-header">
                          <span className="txn-card-type">{txn.type}</span>
                          <span className={`txn-card-amount ${(txn.amount || 0) >= 0 ? 'txn-positive' : 'txn-negative'}`}>
                            {(txn.amount || 0) >= 0 ? '+' : ''}{txn.amount || 0} credits
                          </span>
                        </div>
                        <p className="txn-card-desc">{txn.description}</p>
                        <div className="txn-card-footer">
                          <span className="txn-card-date">{formatDate(txn.created_at)}</span>
                          <span className="txn-card-balance">Balance: {txn.balance_after || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="txn-view-more">
                    <Link to="/transactions" className="btn-view-more">View All Transactions</Link>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default PurchasePage;
